import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../lib/api";
import { ShieldAlert, X } from "lucide-react";
import { ROLES } from "../lib/rbac";

/**
 * Iter76 — Top-of-page banner shown to Admin users when there's an
 * unacknowledged critical security event. Dismissible for the current
 * session; polling every 60s in the background.
 */

const DISMISS_KEY = "asl_security_banner_dismissed";

export default function SecurityBanner() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [event, setEvent] = useState(null);
    const [dismissedId, setDismissedId] = useState(() => {
        try { return localStorage.getItem(DISMISS_KEY) || ""; } catch { return ""; }
    });

    useEffect(() => {
        if (!user || user.role !== ROLES.KAAFI_ADMIN) return;
        let cancelled = false;
        const load = async () => {
            try {
                const r = await api.get("/admin/security/banner");
                if (!cancelled) setEvent(r.data?.event || null);
            } catch { /* ignore — banner is best-effort */ }
        };
        load();
        const iv = setInterval(load, 60_000);
        return () => { cancelled = true; clearInterval(iv); };
    }, [user]);

    if (!user || user.role !== ROLES.KAAFI_ADMIN) return null;
    if (!event) return null;
    if (event.id === dismissedId) return null;

    const dismiss = () => {
        try { localStorage.setItem(DISMISS_KEY, event.id); } catch {}
        setDismissedId(event.id);
    };

    return (
        <div
            className="bg-rose-50 border-b border-rose-200 text-rose-900 px-6 py-2.5 flex items-center gap-3 text-sm"
            data-testid="security-critical-banner"
        >
            <ShieldAlert className="w-4 h-4 flex-shrink-0" />
            <div className="flex-1">
                <span className="font-medium">Critical security event:</span>{" "}
                {event.event_type?.replaceAll("_", " ")} · {event.details?.email || event.user_id || event.ip || "unknown"}
            </div>
            <button
                className="text-rose-800 hover:underline text-xs font-medium"
                onClick={() => navigate("/admin/security")}
                data-testid="security-banner-open"
            >
                Open Security Dashboard →
            </button>
            <button onClick={dismiss} className="text-rose-500 hover:text-rose-900" data-testid="security-banner-dismiss">
                <X className="w-3.5 h-3.5" />
            </button>
        </div>
    );
}
